import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import {
  alCambiarElUltimoSaldo,
  leerSaldo,
  ultimoSaldoLeido,
  type Saldo,
} from '../lib/saldo';

/**
 * Saldo de la empresa o de la marca activa (según la bandera). Se refresca solo cuando se
 * gastan/cargan créditos (`atlas:credits-updated`) y cuando cambia la marca (`atlas:brand-changed`).
 */
export function useSaldo(idCompany: string | null): { saldo: Saldo | null; recargar: () => void } {
  const [saldo, setSaldo] = useState<Saldo | null>(null);
  const [tick, setTick] = useState(0);
  const recargar = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    window.addEventListener('atlas:credits-updated', recargar);
    window.addEventListener('atlas:brand-changed', recargar);
    return () => {
      window.removeEventListener('atlas:credits-updated', recargar);
      window.removeEventListener('atlas:brand-changed', recargar);
    };
  }, [recargar]);

  useEffect(() => {
    if (!idCompany) {
      setSaldo(null);
      return;
    }
    let vivo = true;
    leerSaldo(idCompany)
      .then((s) => {
        if (vivo) setSaldo(s);
      })
      .catch(() => {});
    return () => {
      vivo = false;
    };
  }, [idCompany, tick]);

  return { saldo, recargar };
}

/**
 * El último saldo que leyó cualquiera (`lib/saldo`), sin consultar: alcanza para saber si la
 * empresa factura por marca y cuál es la marca activa. `null` hasta la primera lectura.
 */
export function useUltimoSaldo(): Saldo | null {
  return useSyncExternalStore(alCambiarElUltimoSaldo, ultimoSaldoLeido, () => null);
}
